import React, { useState } from "react";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { Box, TextField, CircularProgress, Snackbar, Alert } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import Cookie from "js-cookie";
import {
  inputStyle,
  StyledButton,
  StyledAgGridContainer,
  StyledTableContainer,
} from "components/StyledComponents/styledComponents.js";
import { useMaterialUIController } from "context";
import URLs from "constants/urls";

export default function DailyTrack() {
  const [selectedDate, setSelectedDate] = useState("");
  const [attendanceData, setAttendanceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [alertType, setAlertType] = useState("success");
  const [message, setMessage] = useState("");
  const hrms_token = Cookie.get("hrms_token");
  const MInput = inputStyle();

  // Assuming role_id and user_id are stored in cookies.
  // You might need to adjust the cookie names if they are different.
  // const roleId = Cookie.get("role_id");
  // const employeeId = Cookie.get("user_id");
  const [controller] = useMaterialUIController();
  const { user } = controller;
  const columns = [
    {
      headerName: "Employee Code",
      field: "bioEmployeeId",
      flex: 1,
    },
    {
      headerName: "Employee Name",
      field: "employeeName",
      flex: 1.5,
    },

    {
      headerName: "Date",
      field: "date",
      flex: 1.1,
      valueFormatter: (params) => params.value || "",
    },
    {
      headerName: "First In",
      field: "firstIn",
      flex: 1.5,
      valueFormatter: (params) => {
        if (!params.value) return "";
        const dt = new Date(params.value);
        if (isNaN(dt.getTime())) return params.value;
        const time = dt
          .toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
          })
          .toUpperCase();
        return `${time}`;
      },
    },

    {
      headerName: "Last Out",
      field: "lastOut",
      flex: 1.5,
      valueFormatter: (params) => {
        if (!params.value) return "";
        const dt = new Date(params.value);
        if (isNaN(dt.getTime())) return params.value;
        const time = dt
          .toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
          })
          .toUpperCase();
        return `${time}`;
      },
    },
    {
      headerName: "Duration",
      field: "duration",
      flex: 1.1,
      valueFormatter: (params) => params.value || "",
    },
  ];

  const handleDisplay = async () => {
    if (!selectedDate) {
      setMessage("Please select a date");
      setAlertType("error");
      setOpen(true);
      return;
    }

    if (!user.roleId || !user.employeeRecordId) {
      setMessage("Could not retrieve user role or employee ID. Please log in again.");
      setAlertType("error");
      setOpen(true);
      return;
    }

    setLoading(true);
    setAttendanceData([]);
    try {
      const url = `${URLs.hrmsTimeTrackerUrl}/${user.roleId}/${user.employeeRecordId}/${selectedDate}`;
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${hrms_token}`,
        },
      });
      if (!response.ok) throw new Error("Failed to fetch logs");
      const data = await response.json();
      // Map data if needed, or just setAttendanceData(data) if already in correct format
      const mappedData = Array.isArray(data)
        ? data.map((item) => {
            let dateStr = "";
            if (item.firstIn) {
              const dt = new Date(item.firstIn);
              if (!isNaN(dt.getTime())) {
                const day = String(dt.getDate()).padStart(2, "0");
                const month = String(dt.getMonth() + 1).padStart(2, "0");
                const year = dt.getFullYear();
                dateStr = `${day}-${month}-${year}`;
              }
            }
            return {
              bioEmployeeId: item.bioEmployeeId,
              employeeName: item.employeeName,
              firstIn: item.firstIn,
              lastOut: item.lastOut,
              duration: item.duration,
              date: dateStr,
            };
          })
        : [];
      setAttendanceData(mappedData);
    } catch (error) {
      console.error("Failed to fetch attendance data:", error);
      setMessage("Failed to fetch logs. Check console for details.");
      setAlertType("error");
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", mt: 0 }}>
        <StyledTableContainer
          container
          spacing={1}
          sx={{
            display: "flex",
            backgroundColor: "#ffffff",
            minHeight: "500px",
            borderRadius: "15px",
            overflow: "auto",
            width: "100%",
            mb: 2,
          }}
        >
          <Box sx={{ width: "100%", p: 3 }}>
            <Box sx={{ display: "flex", gap: 2, mb: 2, alignItems: "center", flexWrap: "wrap" }}>
              <TextField
                label="Select Date"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                sx={{ minWidth: 200, ...MInput.b }}
                InputLabelProps={{
                  shrink: true,
                  sx: { color: MInput.a },
                }}
              />
              <StyledButton
                onClick={handleDisplay}
                disabled={!selectedDate || loading}
                sx={{ height: 40 }}
              >
                Display
              </StyledButton>
            </Box>
            <StyledAgGridContainer
              component={Box}
              className="ag-theme-alpine"
              sx={{
                flexGrow: 1,
                backgroundColor: "transparent",
                height: "calc(100vh - 260px)",
                width: "100%",
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                <AgGridReact
                  rowData={attendanceData}
                  columnDefs={columns}
                  pagination={true}
                  paginationPageSize={10}
                  defaultColDef={{ resizable: true, filter: true }}
                  overlayNoRowsTemplate="<span>No attendance records found</span>"
                />
              )}
            </StyledAgGridContainer>
          </Box>
        </StyledTableContainer>
        <Snackbar
          open={open}
          autoHideDuration={5000}
          onClose={() => setOpen(false)}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert onClose={() => setOpen(false)} severity={alertType} sx={{ width: "100%" }}>
            {message}
          </Alert>
        </Snackbar>
      </Box>
    </DashboardLayout>
  );
}
